import { createClient } from '@connectrpc/connect';
import { create, fromBinary, toBinary } from '@bufbuild/protobuf';
import { ValidationService } from './gen/validation_service_pb';
import {
  ExecuteValidationRequestSchema,
  ExecuteValidationResponseSchema,
  GetDocumentInfoRequestSchema,
  GetDocumentInfoResponseSchema,
  GetAvailableValidationsRequestSchema,
  GetAvailableValidationsResponseSchema,
  GetValidationSettingsRequestSchema,
  UpdateValidationSettingsRequestSchema,
  ValidationSettingsResponseSchema,
  GetLastReportRequestSchema,
  GetLastReportResponseSchema,
} from './gen/validation_service_pb';
import { getGrpcBaseUrl, getGrpcTransport } from './transport';
import { pollRevitOperation, type RevitOperationProgressSnapshot } from './operationProgressClient';
import { useIntegrationDiagnosticsStore } from '../../stores/useIntegrationDiagnosticsStore';
import { RevitOperationAcceptanceTimeoutMs } from '../../lib/revit/revitOperationTimeoutPolicy';
import { flushWebLogger, webLog } from './webLogger';
import {
  unaryViaDirectGrpcWeb,
  frameGrpcWebMessage,
  toArrayBuffer,
  parseGrpcWebResponse,
} from './directGrpcWeb';

function getClient() {
  const transport = getGrpcTransport();
  if (!transport) {
    return null;
  }

  return createClient(ValidationService, transport);
}

type AvailableValidationsGrpcResult = {
  success: boolean;
  validations?: Array<{ id: number; title: string; description: string; defaultSeverity: string; category: string; isEnabled: boolean; criticalityRank: number }>;
  error?: string;
};

type LastReportGrpcResult = {
  success: boolean;
  hasReport?: boolean;
  reportJson?: string;
  lastValidationDate?: string;
  error?: string;
};

type DocumentInfoGrpcResult = {
  success: boolean;
  documentName?: string;
  documentPath?: string;
  error?: string;
};

export type ValidationSettingsGrpcResult = {
  success: boolean;
  reconnectGapMarginMillimeters?: number;
  defaultReconnectGapMarginMillimeters?: number;
  minReconnectGapMarginMillimeters?: number;
  maxReconnectGapMarginMillimeters?: number;
  error?: string;
};

const AVAILABLE_VALIDATIONS_CACHE_TTL_MS = 5 * 60 * 1000;
const DOCUMENT_INFO_CACHE_TTL_MS = 5_000;
const EXECUTE_VALIDATION_PATH = '/pipeex.validation.v1.ValidationService/ExecuteValidation';
const DOCUMENT_INFO_FETCH_TIMEOUT_MS = 15_000;
const EXECUTE_VALIDATION_FETCH_TIMEOUT_MS = 10 * 60 * 1000;

let availableValidationsCache: { at: number; result: AvailableValidationsGrpcResult } | null = null;
let availableValidationsInFlight: Promise<AvailableValidationsGrpcResult> | null = null;
let lastReportInFlight: Promise<LastReportGrpcResult> | null = null;
let documentInfoCache: { at: number; result: DocumentInfoGrpcResult } | null = null;
let documentInfoInFlight: Promise<DocumentInfoGrpcResult> | null = null;

type ExecuteValidationGrpcResult = {
  success: boolean;
  reportJson?: string;
  error?: string;
};

type ExecuteValidationGrpcOptions = {
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
};

/**
 * Retries an idempotent gRPC-web read on a TRANSIENT WebView2 transport failure.
 * In the Revit plugin WebView2 the loopback fetch to the embedded Kestrel gRPC
 * server intermittently rejects BEFORE the request leaves the WebView with
 * "Element not found (0x80070490)" / "Failed to fetch" (a stale-connection /
 * networking blip) - a plain curl to the very same endpoint always returns 200,
 * so re-issuing the fetch on a fresh connection recovers. Only retries on those
 * transport signatures; real gRPC errors (status != 0) come back in the response
 * body and are NOT retried here. Safe because all callers are pure reads.
 */
async function retryOnTransient<T>(fn: () => Promise<T>, attempts = 3): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      const message = error instanceof Error ? error.message : String(error);
      const transient = /0x80070490|Element not found|Failed to fetch|load failed|network error|ERR_/i.test(message);
      if (!transient || attempt === attempts - 1) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, 200 * (attempt + 1)));
    }
  }
  throw lastError;
}

export async function getAvailableValidationsGrpc(): Promise<AvailableValidationsGrpcResult> {
  if (
    availableValidationsCache &&
    Date.now() - availableValidationsCache.at < AVAILABLE_VALIDATIONS_CACHE_TTL_MS
  ) {
    return availableValidationsCache.result;
  }

  if (availableValidationsInFlight) {
    return availableValidationsInFlight;
  }

  availableValidationsInFlight = getAvailableValidationsGrpcUncached().then((result) => {
    if (result.success) {
      availableValidationsCache = { at: Date.now(), result };
    }
    return result;
  }).finally(() => {
    availableValidationsInFlight = null;
  });

  return availableValidationsInFlight;
}

async function getAvailableValidationsGrpcUncached(): Promise<AvailableValidationsGrpcResult> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('GetAvailableValidations: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const response = await retryOnTransient(() =>
      unaryViaDirectGrpcWeb(
        '/pipeex.validation.v1.ValidationService/GetAvailableValidations',
        GetAvailableValidationsRequestSchema,
        GetAvailableValidationsResponseSchema,
        {}
      )
    );
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetAvailableValidations: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      validations: response.validations?.map((v) => ({
        id: v.id,
        title: v.title,
        description: v.description,
        defaultSeverity: v.defaultSeverity,
        category: v.category,
        isEnabled: v.isEnabled,
        criticalityRank: v.criticalityRank,
      })),
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'GetAvailableValidations gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetAvailableValidations: ${message}`);
    return { success: false, error: message };
  }
}

export async function executeValidationGrpc(
  configJson: string,
  options?: ExecuteValidationGrpcOptions
): Promise<ExecuteValidationGrpcResult> {
  const operationResult = await executeValidationViaOperation(configJson, options);
  if (operationResult) {
    return operationResult;
  }

  const directResult = await executeValidationViaDirectGrpcWeb(configJson);
  if (directResult) {
    return directResult;
  }

  return executeValidationViaConnectClient(configJson);
}

async function executeValidationViaOperation(
  configJson: string,
  options?: ExecuteValidationGrpcOptions
): Promise<ExecuteValidationGrpcResult | null> {
  const client = getClient();
  if (!client) {
    return null;
  }

  try {
    webLog.info(
      'validationGrpcClient',
      `executeValidation: operation-start configBytes=${configJson.length}`
    );
    void flushWebLogger();
    const startResponse = await client.startValidationRun({ configJson });
    if (!startResponse.isSuccess || !startResponse.operationId) {
      return {
        success: false,
        error: startResponse.error || 'Validation operation did not start',
      };
    }

    const operationResult = await pollRevitOperation(startResponse.operationId, {
      maxRevitWaitMs: RevitOperationAcceptanceTimeoutMs,
      onProgress: options?.onProgress,
      parseResult: parseExecuteValidationOperationResult,
    });
    if (!operationResult.success || !operationResult.result) {
      return {
        success: false,
        error: operationResult.error || 'Validation operation failed',
      };
    }

    const result = operationResult.result;
    if (result.success) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (result.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ExecuteValidation: ${result.error}`);
    }

    return result;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'StartValidationRun gRPC error';
    webLog.warn('validationGrpcClient', `executeValidation: operation unavailable (${message}); falling back`);
    void flushWebLogger();
    return null;
  }
}

function parseExecuteValidationOperationResult(resultJson: string): ExecuteValidationGrpcResult {
  const parsed = JSON.parse(resultJson);
  return {
    success: Boolean(parsed.isSuccess),
    reportJson: parsed.reportJson || undefined,
    error: parsed.error || undefined,
  };
}

async function executeValidationViaConnectClient(configJson: string): Promise<ExecuteValidationGrpcResult> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('ExecuteValidation: gRPC transport is unavailable');
    webLog.error('validationGrpcClient', 'executeValidation: connect transport unavailable');
    void flushWebLogger();
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    webLog.info(
      'validationGrpcClient',
      `executeValidation: connect-request-sent configBytes=${configJson.length}`
    );
    void flushWebLogger();
    const response = await client.executeValidation({ configJson });
    webLog.info(
      'validationGrpcClient',
      `executeValidation: connect-response success=${response.isSuccess} reportBytes=${response.reportJson?.length ?? 0} error=${response.error || ''}`
    );
    void flushWebLogger();
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ExecuteValidation: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      reportJson: response.reportJson || undefined,
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'ExecuteValidation gRPC error';
    webLog.error('validationGrpcClient', `executeValidation: connect-failed ${message}`);
    void flushWebLogger();
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ExecuteValidation: ${message}`);
    return { success: false, error: message };
  }
}

async function executeValidationViaDirectGrpcWeb(configJson: string): Promise<ExecuteValidationGrpcResult | null> {
  const baseUrl = getGrpcBaseUrl();
  if (!baseUrl || typeof fetch !== 'function' || typeof AbortController === 'undefined') {
    return null;
  }

  const request = create(ExecuteValidationRequestSchema, { configJson });
  const requestBytes = toBinary(ExecuteValidationRequestSchema, request);
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), EXECUTE_VALIDATION_FETCH_TIMEOUT_MS);

  try {
    webLog.info(
      'validationGrpcClient',
      `executeValidation: direct-fetch request-start url=${EXECUTE_VALIDATION_PATH} configBytes=${configJson.length} frameBytes=${requestBytes.length}`
    );
    await flushWebLogger();

    const response = await fetch(`${baseUrl}${EXECUTE_VALIDATION_PATH}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/grpc-web+proto',
        'X-Grpc-Web': '1',
        'X-User-Agent': 'pipeex-web',
      },
      body: new Blob([toArrayBuffer(frameGrpcWebMessage(requestBytes))], {
        type: 'application/grpc-web+proto',
      }),
      signal: controller.signal,
    });

    webLog.info(
      'validationGrpcClient',
      `executeValidation: direct-fetch http-response status=${response.status} ok=${response.ok}`
    );
    await flushWebLogger();

    const responseBytes = new Uint8Array(await response.arrayBuffer());
    const parsedResponse = parseGrpcWebResponse(responseBytes);
    webLog.info(
      'validationGrpcClient',
      `executeValidation: direct-fetch parsed responseBytes=${responseBytes.length} messageBytes=${parsedResponse.messageBytes?.length ?? 0} grpcStatus=${parsedResponse.grpcStatus ?? ''}`
    );
    await flushWebLogger();

    if (!response.ok) {
      const message = parsedResponse.grpcMessage || `HTTP ${response.status}`;
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ExecuteValidation: ${message}`);
      return { success: false, error: message };
    }

    if (parsedResponse.grpcStatus && parsedResponse.grpcStatus !== '0') {
      const message = parsedResponse.grpcMessage || `gRPC status ${parsedResponse.grpcStatus}`;
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ExecuteValidation: ${message}`);
      return { success: false, error: message };
    }

    if (!parsedResponse.messageBytes) {
      const message = 'ExecuteValidation response did not contain a data frame';
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ExecuteValidation: ${message}`);
      return { success: false, error: message };
    }

    const decoded = fromBinary(ExecuteValidationResponseSchema, parsedResponse.messageBytes);
    webLog.info(
      'validationGrpcClient',
      `executeValidation: direct-fetch decoded success=${decoded.isSuccess} reportBytes=${decoded.reportJson?.length ?? 0} error=${decoded.error || ''}`
    );
    await flushWebLogger();

    if (decoded.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (decoded.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ExecuteValidation: ${decoded.error}`);
    }

    return {
      success: decoded.isSuccess,
      reportJson: decoded.reportJson || undefined,
      error: decoded.error || undefined,
    };
  } catch (error) {
    const isAbort = error instanceof DOMException && error.name === 'AbortError';
    const message = isAbort
      ? `ExecuteValidation request did not reach a response within ${Math.round(EXECUTE_VALIDATION_FETCH_TIMEOUT_MS / 1000)}s`
      : error instanceof Error
        ? error.message
        : 'ExecuteValidation direct gRPC-web error';
    webLog.error('validationGrpcClient', `executeValidation: direct-fetch failed ${message}`);
    await flushWebLogger();
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ExecuteValidation: ${message}`);
    return { success: false, error: message };
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export async function getLastReportGrpc(): Promise<LastReportGrpcResult> {
  if (lastReportInFlight) {
    return lastReportInFlight;
  }

  lastReportInFlight = getLastReportGrpcUncached().finally(() => {
    lastReportInFlight = null;
  });

  return lastReportInFlight;
}

async function getLastReportGrpcUncached(): Promise<LastReportGrpcResult> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('GetLastReport: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const response = await retryOnTransient(() =>
      unaryViaDirectGrpcWeb(
        '/pipeex.validation.v1.ValidationService/GetLastReport',
        GetLastReportRequestSchema,
        GetLastReportResponseSchema,
        {}
      )
    );
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetLastReport: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      hasReport: response.hasReport,
      reportJson: response.reportJson || undefined,
      lastValidationDate: response.lastValidationDate || undefined,
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'GetLastReport gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetLastReport: ${message}`);
    return { success: false, error: message };
  }
}

export async function getDocumentInfoGrpc(): Promise<DocumentInfoGrpcResult> {
  if (documentInfoCache && Date.now() - documentInfoCache.at < DOCUMENT_INFO_CACHE_TTL_MS) {
    return documentInfoCache.result;
  }

  if (documentInfoInFlight) {
    return documentInfoInFlight;
  }

  documentInfoInFlight = getDocumentInfoGrpcUncached().then((result) => {
    if (result.success) {
      documentInfoCache = { at: Date.now(), result };
    }
    return result;
  }).finally(() => {
    documentInfoInFlight = null;
  });

  return documentInfoInFlight;
}

async function getDocumentInfoGrpcUncached(): Promise<DocumentInfoGrpcResult> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('GetDocumentInfo: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const response = await retryOnTransient(() =>
      unaryViaDirectGrpcWeb(
        '/pipeex.validation.v1.ValidationService/GetDocumentInfo',
        GetDocumentInfoRequestSchema,
        GetDocumentInfoResponseSchema,
        {},
        undefined,
        DOCUMENT_INFO_FETCH_TIMEOUT_MS
      )
    );
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else {
      console.warn('[validationGrpcClient] GetDocumentInfo: no document — %s', response.error);
      if (response.error) {
        useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetDocumentInfo: ${response.error}`);
      }
    }
    return {
      success: response.isSuccess,
      documentName: response.documentName || undefined,
      documentPath: response.documentPath || undefined,
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'GetDocumentInfo gRPC error';
    console.error('[validationGrpcClient] GetDocumentInfo error:', message);
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetDocumentInfo: ${message}`);
    return { success: false, error: message };
  }
}

function mapValidationSettingsResponse(response: {
  isSuccess: boolean;
  error: string;
  reconnectGapMarginMillimeters: number;
  defaultReconnectGapMarginMillimeters: number;
  minReconnectGapMarginMillimeters: number;
  maxReconnectGapMarginMillimeters: number;
}): ValidationSettingsGrpcResult {
  return {
    success: response.isSuccess,
    reconnectGapMarginMillimeters: response.reconnectGapMarginMillimeters,
    defaultReconnectGapMarginMillimeters: response.defaultReconnectGapMarginMillimeters,
    minReconnectGapMarginMillimeters: response.minReconnectGapMarginMillimeters,
    maxReconnectGapMarginMillimeters: response.maxReconnectGapMarginMillimeters,
    error: response.error || undefined,
  };
}

export async function getValidationSettingsGrpc(): Promise<ValidationSettingsGrpcResult> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('GetValidationSettings: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const response = await retryOnTransient(() =>
      unaryViaDirectGrpcWeb(
        '/pipeex.validation.v1.ValidationService/GetValidationSettings',
        GetValidationSettingsRequestSchema,
        ValidationSettingsResponseSchema,
        {}
      )
    );
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetValidationSettings: ${response.error}`);
    }
    return mapValidationSettingsResponse(response);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'GetValidationSettings gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetValidationSettings: ${message}`);
    return { success: false, error: message };
  }
}

export async function updateValidationSettingsGrpc(
  reconnectGapMarginMillimeters: number
): Promise<ValidationSettingsGrpcResult> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('UpdateValidationSettings: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const response = await retryOnTransient(() =>
      unaryViaDirectGrpcWeb(
        '/pipeex.validation.v1.ValidationService/UpdateValidationSettings',
        UpdateValidationSettingsRequestSchema,
        ValidationSettingsResponseSchema,
        { reconnectGapMarginMillimeters }
      )
    );
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`UpdateValidationSettings: ${response.error}`);
    }
    return mapValidationSettingsResponse(response);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'UpdateValidationSettings gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`UpdateValidationSettings: ${message}`);
    return { success: false, error: message };
  }
}

export async function highlightElementsGrpc(elementIds: bigint[]): Promise<{
  success: boolean;
  count?: number;
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('HighlightElements: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const response = await client.highlightElements({ elementIds });
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`HighlightElements: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      count: response.count,
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'HighlightElements gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`HighlightElements: ${message}`);
    return { success: false, error: message };
  }
}

export async function exportReportJsonGrpc(): Promise<{
  success: boolean;
  content?: string;
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('ExportReportJson: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const response = await client.exportReportJson({});
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ExportReportJson: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      content: response.content || undefined,
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'ExportReportJson gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ExportReportJson: ${message}`);
    return { success: false, error: message };
  }
}

export async function exportReportHtmlGrpc(): Promise<{
  success: boolean;
  content?: string;
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('ExportReportHtml: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const response = await client.exportReportHtml({});
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ExportReportHtml: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      content: response.content || undefined,
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'ExportReportHtml gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ExportReportHtml: ${message}`);
    return { success: false, error: message };
  }
}

export async function applyQuickFixGrpc(
  fixDataJson: string,
  options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void }
): Promise<{
  success: boolean;
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('ApplyQuickFix: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const request = { fixDataJson };
    if (options?.onProgress) {
      try {
        const startResponse = await client.startApplyQuickFix(request);
        if (startResponse.isSuccess && startResponse.operationId) {
          const operationResult = await pollRevitOperation(startResponse.operationId, {
            onProgress: options.onProgress,
            parseResult: parseStandardOperationResult,
          });
          if (!operationResult.success || !operationResult.result) {
            const error = operationResult.error || 'ApplyQuickFix operation failed';
            useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ApplyQuickFix: ${error}`);
            return { success: false, error };
          }
          return operationResult.result;
        }
      } catch (startError) {
        const message = startError instanceof Error ? startError.message : 'StartApplyQuickFix gRPC error';
        webLog.warn('validationGrpcClient', `applyQuickFix: operation unavailable (${message}); falling back`);
      }
    }

    const response = await client.applyQuickFix(request);
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ApplyQuickFix: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'ApplyQuickFix gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ApplyQuickFix: ${message}`);
    return { success: false, error: message };
  }
}

function parseStandardOperationResult(resultJson: string): {
  success: boolean;
  error?: string;
} {
  const parsed = JSON.parse(resultJson);
  return {
    success: Boolean(parsed.isSuccess),
    error: parsed.error || undefined,
  };
}

export async function getQuickFixResultGrpc(): Promise<{
  success: boolean;
  status?: string;
  resultJson?: string;
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('GetQuickFixResult: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const response = await client.getQuickFixResult({});
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetQuickFixResult: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      status: response.status || undefined,
      resultJson: response.resultJson || undefined,
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'GetQuickFixResult gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetQuickFixResult: ${message}`);
    return { success: false, error: message };
  }
}

export async function reconnectElementsGrpc(
  elementId1: bigint,
  elementId2: bigint,
  options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void }
): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('ReconnectElements: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const request = { elementId1, elementId2 };
    if (options?.onProgress) {
      try {
        const startResponse = await client.startReconnectElements(request);
        if (startResponse.isSuccess && startResponse.operationId) {
          const operationResult = await pollRevitOperation(startResponse.operationId, {
            onProgress: options.onProgress,
            parseResult: parseReconnectElementsResultJson,
          });
          if (!operationResult.success || !operationResult.result) {
            const error = operationResult.error || 'ReconnectElements operation failed';
            useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ReconnectElements: ${error}`);
            return { success: false, error };
          }
          return operationResult.result;
        }
      } catch (startError) {
        const message = startError instanceof Error ? startError.message : 'StartReconnectElements gRPC error';
        webLog.warn('validationGrpcClient', `reconnectElements: operation unavailable (${message}); falling back`);
      }
    }

    const response = await client.reconnectElements(request);
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ReconnectElements: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      message: response.message || undefined,
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'ReconnectElements gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ReconnectElements: ${message}`);
    return { success: false, error: message };
  }
}

function parseReconnectElementsResultJson(resultJson: string): {
  success: boolean;
  message?: string;
  error?: string;
} {
  const parsed = JSON.parse(resultJson);
  return {
    success: Boolean(parsed.isSuccess),
    message: parsed.message || undefined,
    error: parsed.error || undefined,
  };
}

export async function reconnectAllPairsGrpc(
  validationId: number,
  options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void }
): Promise<{
  success: boolean;
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('ReconnectAllPairs: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const request = { validationId };
    if (options?.onProgress) {
      try {
        const startResponse = await client.startReconnectAllPairs(request);
        if (startResponse.isSuccess && startResponse.operationId) {
          const operationResult = await pollRevitOperation(startResponse.operationId, {
            onProgress: options.onProgress,
            parseResult: parseStandardOperationResult,
          });
          if (!operationResult.success || !operationResult.result) {
            const error = operationResult.error || 'ReconnectAllPairs operation failed';
            useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ReconnectAllPairs: ${error}`);
            return { success: false, error };
          }
          return operationResult.result;
        }
      } catch (startError) {
        const message = startError instanceof Error ? startError.message : 'StartReconnectAllPairs gRPC error';
        webLog.warn('validationGrpcClient', `reconnectAllPairs: operation unavailable (${message}); falling back`);
      }
    }

    const response = await client.reconnectAllPairs(request);
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ReconnectAllPairs: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'ReconnectAllPairs gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`ReconnectAllPairs: ${message}`);
    return { success: false, error: message };
  }
}

export async function getReconnectionResultGrpc(): Promise<{
  success: boolean;
  status?: string;
  resultJson?: string;
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('GetReconnectionResult: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const response = await client.getReconnectionResult({});
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetReconnectionResult: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      status: response.status || undefined,
      resultJson: response.resultJson || undefined,
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'GetReconnectionResult gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetReconnectionResult: ${message}`);
    return { success: false, error: message };
  }
}
